import { useCallback, useEffect, useState } from 'react';
import { useI18n } from 'context/i18n';
import { useUser } from 'context/user';
import { useLocale } from 'hooks/useLocale';
import { COOKIES, getCookie, getFixedPrice } from 'utils/helpers';
import { getPriceRange, getPreviousPriceRange, getDiscountString, obtainVoucherPricesV2 } from 'utils/price';
import type { TPrice } from 'types/product';
import { Skeleton } from 'components/ui/skeleton';

export function Price({ price, voucherPrices, className }: { price: TPrice; voucherPrices?: Record<string, TPrice>; className?: string }) {
  const { t, profileData } = useI18n();
  const { country, locale } = useLocale();
  const { shouldRecalculateVouchers, contextFilters, activatedPrevouchers, handleActivatePrevoucher } = useUser();
  const [isCalculating, setIsCalculating] = useState(false);
  const [currentPriceData, setCurrentPriceData] = useState<TPrice>(price);
  const [touristMode, setTouristMode] = useState(false);
  const [voucherPriceText, setVoucherPriceText] = useState<string | null>(null);
  const [appliedVoucher, setAppliedVoucher] = useState(false);

  // Check for tourist mode
  useEffect(() => {
    const countryPrice = getCookie(COOKIES.COUNTRY_PRICE);
    setTouristMode(countryPrice !== null && countryPrice !== country);
  }, [country]);

  // Voucher calculation logic
  const calculateVoucherPrice = useCallback(() => {
    if (typeof window === 'undefined') return;
    const voucherPriceInfo = obtainVoucherPricesV2({
      data: { ...price, voucherPrices: voucherPrices ?? {} },
      t,
      locale,
    });
    if (voucherPriceInfo !== null) {
      setIsCalculating(true);
      const { message, info } = voucherPriceInfo;
      if (message) {
        setVoucherPriceText(message);
      } else {
        const infoPriceData = { ...info };
        // Handle rare case for price per size
        if (
          infoPriceData.pricePerSize &&
          !infoPriceData?.priceDiscountFromFormatted &&
          !infoPriceData?.priceDiscountToFormatted &&
          (infoPriceData as any)?.voucherRangePrices?.priceFromFormatted
        ) {
          infoPriceData.priceDiscountFromFormatted = (infoPriceData as any)?.voucherRangePrices?.priceFromFormatted;
          infoPriceData.priceDiscountToFormatted = (infoPriceData as any)?.voucherRangePrices?.priceToFormatted;
          if (price?.priceFromFormatted) {
            infoPriceData.priceFromFormatted = price?.priceFromFormatted;
          }
          if (price?.priceToFormatted) {
            infoPriceData.priceToFormatted = price?.priceToFormatted;
          }
        }
        setCurrentPriceData({ ...infoPriceData, discountFromVoucher: true });
        setAppliedVoucher(true);
        setVoucherPriceText(null);
      }
    } else {
      setAppliedVoucher(false);
      setVoucherPriceText(null);
      setCurrentPriceData(price);
    }
    setTimeout(() => {
      setIsCalculating(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, voucherPrices, t, locale, contextFilters]);

  // Recalculate on relevant changes
  useEffect(() => {
    if (shouldRecalculateVouchers) {
      calculateVoucherPrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRecalculateVouchers]);

  useEffect(() => {
    setCurrentPriceData(price);
    calculateVoucherPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, calculateVoucherPrice]);

  // Data for rendering
  const fixedPrice = getFixedPrice(country, profileData, currentPriceData?.current);
  const priceRange = getPriceRange(currentPriceData);
  const previousRange = getPreviousPriceRange(currentPriceData);
  const priceDiscountString = getDiscountString(currentPriceData);
  const previous = currentPriceData.previous;
  const currentFormated = currentPriceData.currentFormated;
  const previousFormated = currentPriceData.previousFormated;
  const installments = currentPriceData.installments;
  const appliedVoucherState = appliedVoucher;

  // Render

  if (isCalculating) {
    return (
      <div className={className}>
        <Skeleton className="h-4 min-w-[30%]" />
        <Skeleton className="h-4 min-w-[30%]" />
      </div>
    );
  }

  return (
    <div className={className}>
      {!touristMode ?
        <div className="leading-[1em] lab:flex">
          <p data-fp={fixedPrice} data-ge-price="data-ge-price" className="mb-0 text-[0.875em] text-copy">
            {currentPriceData.pricePerSize ? priceRange : currentFormated}
          </p>
          {previous !== undefined ?
            <p className={'mb-0 hidden gap-1.5 text-[0.75em] text-[--color-disabled-primary] lab:inline-flex'}>
              <span
                className={`ml-[5px] uppercase lab:ml-0 ${appliedVoucherState && currentPriceData?.discountFromVoucher ? 'text-success lab:text-[#a3a3a3]' : ''}`}
              >
                {priceDiscountString}
              </span>
              <s>{currentPriceData.pricePerSize ? previousRange : previousFormated}</s>
            </p>
          : null}
        </div>
      : null}

      {previous !== undefined ?
        <p className="mb-0 flex gap-1 text-[0.75em] text-[--color-disabled-primary] lab:hidden">
          <s>{currentPriceData.pricePerSize ? previousRange : previousFormated}</s>
          <span className={`uppercase text-success lab:ml-0 lab:text-[#a3a3a3]`}>{priceDiscountString}</span>
        </p>
      : null}
      {previous !== undefined && appliedVoucherState && currentPriceData?.discountFromVoucher && voucherPriceText === null ?
        <p className="text-[0.625em] text-success lab:font-bold lab:text-[#a3a3a3]">{currentPriceData.appliedLabel}</p>
      : null}
      {voucherPriceText ?
        <p className="text-[0.625em] text-copy [&_a]:font-semibold [&_a]:underline" dangerouslySetInnerHTML={{ __html: voucherPriceText }} />
      : null}
      {currentPriceData?.pricePerSize && (
        <p className="mb-0 text-[0.75em] text-[#929292]">{t('listado.productos', 'final.price.acording', 'Final price acording to size')}</p>
      )}
      {installments && <p className="mb-0 text-[0.75em] text-copy">{`${t('listado.productos', 'plazos.pago')} ${installments}`}</p>}
    </div>
  );
}
